import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Sparkles, Package, ClipboardCheck, ShoppingBag, BarChart3,
  Heart, LogOut, ChevronRight, CalendarClock, Share2, Gauge, Flame, Store, Boxes, PackageCheck, Workflow, Calculator, Receipt, Megaphone, Newspaper, ClipboardList,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/ideas", label: "Idea Engine", icon: Sparkles, id: "nav-ideas" },
  { to: "/opportunities", label: "Opportunities", icon: Flame, id: "nav-opportunities" },
  { to: "/calendar", label: "Calendar Engine", icon: CalendarClock, id: "nav-calendar" },
  { to: "/products", label: "Products", icon: Package, id: "nav-products" },
  // Approval Queue hidden — legacy Etsy-direct review board, superseded by Products publish flows.
  // { to: "/approval", label: "Approval Queue", icon: ClipboardCheck, id: "nav-approval" },
  { to: "/distribution", label: "Distribution", icon: Store, id: "nav-distribution" },
  { to: "/marketing", label: "Marketing Studio", icon: Megaphone, id: "nav-marketing" },
  { to: "/blog", label: "Blog Studio", icon: Newspaper, id: "nav-blog" },
  { to: "/social", label: "Social", icon: Share2, id: "nav-social" },
  { to: "/orders", label: "Orders", icon: ShoppingBag, id: "nav-orders" },
  { to: "/store-orders", label: "Store Orders", icon: PackageCheck, id: "nav-store-orders" },
  { to: "/wholesale", label: "Wholesale", icon: Boxes, id: "nav-wholesale" },
  { to: "/rangeme", label: "RangeMe Helper", icon: ClipboardList, id: "nav-rangeme" },
  { to: "/wholesale-margins", label: "Wholesale Margins", icon: Calculator, id: "nav-wholesale-margins" },
  { to: "/walmart-margins", label: "Walmart Margins", icon: Calculator, id: "nav-walmart-margins" },
  { to: "/retail-margins", label: "Retail Margins", icon: Receipt, id: "nav-retail-margins" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, id: "nav-analytics" },
  { to: "/admin", label: "Admin & Cost", icon: Gauge, id: "nav-admin" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-zinc-50 text-zinc-900">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-zinc-200 bg-white flex flex-col fixed h-screen" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-zinc-200">
          <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center">
            <Heart className="w-4.5 h-4.5 text-white" fill="white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[15px] tracking-tight">Heartline</div>
            <div className="text-[10px] text-zinc-400 -mt-0.5">Multi-channel Product Engine</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
          <a
            href="/heartline-workflow.html"
            target="_blank"
            rel="noreferrer"
            data-testid="nav-workflow"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors duration-200"
          >
            <Workflow className="w-4 h-4" />
            Workflow
            <ChevronRight className="w-3.5 h-3.5 ml-auto -rotate-45 text-zinc-300" />
          </a>
        </nav>
        <div className="px-3 py-3 border-t border-zinc-200 text-[11px] text-zinc-400">
          Multi-channel · Print-on-demand
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-60 flex flex-col min-h-screen">
        <header className="h-16 shrink-0 border-b border-zinc-200 bg-white/80 backdrop-blur sticky top-0 z-20 flex items-center justify-between px-6">
          <div className="flex items-center gap-1.5 text-sm text-zinc-400">
            <span>Workspace</span>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-zinc-700 font-medium">Heartline</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 hover:bg-zinc-100 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                    {user?.name?.[0] || "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium hidden sm:block">{user?.name || user?.email}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-rose-600">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
